/**
 * AuditFirmEngagements — /w/:slug/app/settings/audit-firms
 *
 * V98-6 / ADR-0047 R1: the client's only roster lever is the FIRM — invite
 * one for an audit period, withdraw the invitation, or revoke client access.
 * The firm's own partner or manager chooses its people.
 *
 * Track 6 review:
 *   T6-B11 the invite form takes a report date.
 *   T6-B12 no audit periods → an inline "Create period" form; no firm found →
 *          "No firm found for '…'" with "Invite by email".
 *   T6-B13 Withdraw runs through busy + error handling (inline Alert) and
 *          announces success.
 *   T6-H9  owners revoke access here too (custody explained, reason
 *          required); a revoked engagement shows a badge.
 *   Medium "N firms found" is announced; focus moves to "Change" after a pick
 *          and back to the search field after Change.
 */
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Button, Card, EmptyState, Field, Input, Select, Spinner } from '@aigovops/design-system';
import { ActionDialog, useAnnounce } from '../components/today/shared';
import { useOptionalWorkspace } from '../lib/workspace';
import { clientFirmApi, PROGRAM_LABEL } from '../lib/firm-api';
import { fmtDate, fmtRange, plural } from '../lib/format';
import { Chip, errText } from './firm/firm-kit';
import './firm.css';
import './today.css';

export function AuditFirmEngagements() {
  const { slug = '' } = useParams();
  const ws = useOptionalWorkspace();
  const canInvite = ws?.role === 'owner' || ws?.role === 'admin';
  const isOwner = ws?.role === 'owner';
  const qc = useQueryClient();
  const [announcer, announce] = useAnnounce();
  const list = useQuery({ queryKey: ['firm-engagements', slug], queryFn: () => clientFirmApi.list(slug) });
  const [busy, setBusy] = useState<string | null>(null);
  const [rowErr, setRowErr] = useState<{ id: string; text: string } | null>(null);
  const [revoke, setRevoke] = useState<{ id: string; firm: string } | null>(null);
  const refresh = () => qc.invalidateQueries({ queryKey: ['firm-engagements', slug] });
  const withdraw = async (id: string, firm: string) => {
    if (busy) return;
    setBusy(id);
    setRowErr(null);
    try {
      await clientFirmApi.withdraw(slug, id);
      await refresh();
      announce(`Invitation to ${firm} withdrawn`);
    } catch (e) {
      setRowErr({ id, text: errText(e) });
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="gp-firm-stack">
      <Card title="Audit firms">
        {list.isLoading ? <Spinner label="Loading" /> : list.error ? <Alert variant="error">{errText(list.error)}</Alert> : (
          list.data && list.data.engagements.length + list.data.invitations.filter((i) => i.status === 'pending').length > 0 ? (
            <ul className="gp-firm-list">
              {list.data.engagements.map((e) => (
                <li key={e.engagement_id} className="gp-firm-row">
                  <div className="gp-firm-row-head">
                    <h3 className="gp-firm-row-title">{e.firm_name}</h3>
                    {e.revoked_at ? <Chip tone="danger">Access revoked {fmtDate(e.revoked_at)}</Chip> : <Chip tone="success">Engaged</Chip>}
                  </div>
                  <p className="gp-firm-muted">Engagement {fmtRange(e.engagement_start, e.engagement_end)}{e.report_date ? ` · report ${fmtDate(e.report_date)}` : ''}</p>
                  <div className="gp-firm-actions gp-firm-links">
                    <Link to={`/w/${slug}/app/audit-firms/${e.engagement_id}`}>Open engagement</Link>
                    <Link to={`/w/${slug}/app/audit-firms/${e.engagement_id}?preview=auditor`}>Preview as auditor</Link>
                    {isOwner && !e.revoked_at ? <Button compact variant="ghost" onClick={() => setRevoke({ id: e.engagement_id, firm: e.firm_name })}>Revoke access…</Button> : null}
                  </div>
                </li>
              ))}
              {list.data.invitations.filter((i) => i.status === 'pending').map((i) => (
                <li key={i.invitation_id} className="gp-firm-row">
                  <div className="gp-firm-row-head">
                    <h3 className="gp-firm-row-title">{i.firm_name}</h3>
                    <Chip>Invited {fmtDate(i.invited_at)}</Chip>
                  </div>
                  <p className="gp-firm-muted">Engagement {fmtRange(i.engagement_start, i.engagement_end)}{i.report_date ? ` · report ${fmtDate(i.report_date)}` : ''}</p>
                  {rowErr?.id === i.invitation_id ? <Alert variant="error">{rowErr.text}</Alert> : null}
                  {canInvite ? (
                    <div className="gp-firm-actions">
                      <Button compact aria-disabled={busy !== null || undefined} aria-label={`Withdraw the invitation to ${i.firm_name}`} onClick={() => void withdraw(i.invitation_id, i.firm_name)}>{busy === i.invitation_id ? 'Withdrawing…' : 'Withdraw'}</Button>
                    </div>
                  ) : null}
                </li>
              ))}
            </ul>
          ) : <EmptyState title="No audit firm yet" description={canInvite ? undefined : 'An owner or admin invites your auditor.'} action={canInvite ? <a href="#firm-q">Invite your auditor</a> : undefined} />
        )}
      </Card>
      {canInvite ? <InviteFirm slug={slug} onInvited={(firm) => { void refresh(); announce(`${firm} invited`); }} /> : null}
      {revoke ? <RevokeDialog slug={slug} engagementId={revoke.id} firm={revoke.firm} onClose={() => setRevoke(null)} onDone={() => { void refresh(); announce(`${revoke.firm}’s access revoked`); }} /> : null}
      {announcer}
    </div>
  );
}

function InviteFirm({ slug, onInvited }: { slug: string; onInvited: (firm: string) => void }) {
  const [q, setQ] = useState('');
  const [debounced, setDebounced] = useState('');
  const [firm, setFirm] = useState<{ firm_id: string; name: string } | null>(null);
  const [form, setForm] = useState({ audit_period_id: '', engagement_start: '', engagement_end: '', report_date: '' });
  const [tried, setTried] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const changeRef = useRef<HTMLButtonElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const focusAfter = useRef<'change' | 'search' | null>(null);
  const periods = useQuery({ queryKey: ['audit-periods', slug], queryFn: () => clientFirmApi.periods(slug) });
  useEffect(() => { const t = setTimeout(() => setDebounced(q.trim()), 250); return () => clearTimeout(t); }, [q]);
  const found = useQuery({ queryKey: ['firm-lookup', debounced], queryFn: () => clientFirmApi.lookup(debounced), enabled: debounced.length >= 2 && !firm });
  useEffect(() => {
    if (focusAfter.current === 'change') changeRef.current?.focus();
    if (focusAfter.current === 'search') searchRef.current?.focus();
    focusAfter.current = null;
  }, [firm]);

  const errs = {
    firm: !firm ? 'Choose a firm.' : null,
    period: !form.audit_period_id ? 'Choose the audit period.' : null,
    start: !form.engagement_start ? 'When does the engagement start?' : null,
    end: !form.engagement_end ? 'When does fieldwork end?' : form.engagement_start && form.engagement_end <= form.engagement_start ? 'Fieldwork ends after it starts.' : null,
    report: form.report_date && form.engagement_start && form.report_date < form.engagement_start ? 'The report date can’t precede the engagement start.' : null,
  };
  const invite = async (e: FormEvent) => {
    e.preventDefault();
    setTried(true);
    if (Object.values(errs).some(Boolean) || busy || !firm) return;
    setBusy(true);
    setErr(null);
    try {
      await clientFirmApi.invite(slug, { firm_id: firm.firm_id, ...form, report_date: form.report_date || null });
      onInvited(firm.name);
      setFirm(null); setQ(''); setForm({ audit_period_id: '', engagement_start: '', engagement_end: '', report_date: '' }); setTried(false);
    } catch (x) {
      setErr(errText(x));
    } finally {
      setBusy(false);
    }
  };
  const results = found.data?.firms ?? [];
  const mailto = `mailto:?subject=${encodeURIComponent('Join us on GAC Pro for our audit')}&body=${encodeURIComponent(`We’d like to run our audit with you on GAC Pro. Register your firm at https://gacpro.io/audit-firm and we’ll invite you from there.`)}`;
  return (
    <Card title="Invite a firm">
      {err ? <Alert variant="error">{err}</Alert> : null}
      <form className="gp-firm-form" onSubmit={invite} noValidate>
        {firm ? (
          <div className="gp-firm-actions">
            <span>Firm: <strong>{firm.name}</strong></span>
            <Button compact variant="ghost" ref={changeRef} onClick={() => { focusAfter.current = 'search'; setFirm(null); }}>Change</Button>
          </div>
        ) : (
          <>
            <Field id="firm-q" label="Firm" hint="Search by name or verified domain" error={tried ? errs.firm : null}>
              <Input ref={searchRef} value={q} onChange={(e) => setQ(e.target.value)} placeholder="e.g. Ledger & Co" autoComplete="off" />
            </Field>
            <p className="gp-sr-only" role="status" aria-live="polite">{debounced.length >= 2 && found.data ? (results.length ? `${plural(results.length, 'firm')} found` : `No firm found for “${debounced}”`) : ''}</p>
            {debounced.length >= 2 && found.data ? (
              results.length ? (
                <ul className="gp-firm-list" aria-label="Firms found">
                  {results.map((f) => <li key={f.firm_id}><Button compact onClick={() => { focusAfter.current = 'change'; setFirm(f); }}>{f.name}{f.bound_to ?? f.verified_domain ? ` · ${f.bound_to ?? f.verified_domain}` : ''}</Button></li>)}
                </ul>
              ) : (
                <p className="gp-firm-actions"><span>No firm found for “{debounced}”.</span> <a href={mailto}>Invite by email</a></p>
              )
            ) : null}
          </>
        )}
        <PeriodField slug={slug} periods={periods.data?.periods ?? null} loading={periods.isLoading} value={form.audit_period_id} error={tried ? errs.period : null}
          onChange={(v) => setForm({ ...form, audit_period_id: v })} />
        <div className="gp-firm-form--row">
          <Field id="firm-start" label="Engagement starts" error={tried ? errs.start : null}><Input type="date" value={form.engagement_start} onChange={(e) => setForm({ ...form, engagement_start: e.target.value })} /></Field>
          <Field id="firm-end" label="Fieldwork ends" error={tried ? errs.end : null}><Input type="date" value={form.engagement_end} onChange={(e) => setForm({ ...form, engagement_end: e.target.value })} /></Field>
          <Field id="firm-report" label="Report date (optional)" hint="Your auditor can set it later." error={tried ? errs.report : null}><Input type="date" value={form.report_date} onChange={(e) => setForm({ ...form, report_date: e.target.value })} /></Field>
        </div>
        <div className="gp-firm-actions"><Button type="submit" variant="primary" aria-disabled={busy || undefined}>{busy ? 'Inviting…' : 'Invite firm'}</Button></div>
      </form>
    </Card>
  );
}

/** T6-B12: the period picker — or, with none, an inline "Create period" form. */
function PeriodField({ slug, periods, loading, value, error, onChange }: { slug: string; periods: Array<{ period_id: string; period_start: string; period_end: string }> | null; loading: boolean; value: string; error: string | null; onChange: (v: string) => void }) {
  const qc = useQueryClient();
  const profiles = useQuery({ queryKey: ['cert-profiles', slug], queryFn: () => clientFirmApi.profiles(slug), enabled: periods !== null && periods.length === 0 });
  const [creating, setCreating] = useState(false);
  const [p, setP] = useState({ profile_id: '', period_start: '', period_end: '' });
  const [err, setErr] = useState<string | null>(null);
  const [tried, setTried] = useState(false);
  if (loading) return <Spinner label="Loading periods" />;
  const none = periods !== null && periods.length === 0;
  const create = async () => {
    setTried(true);
    if (!p.profile_id || !p.period_start || !p.period_end || p.period_end <= p.period_start) return;
    setErr(null);
    try {
      const r = await clientFirmApi.createPeriod(slug, { profile_id: p.profile_id, period_start: p.period_start, period_end: p.period_end });
      await qc.invalidateQueries({ queryKey: ['audit-periods', slug] });
      onChange(r.period.period_id);
      setCreating(false);
    } catch (x) {
      setErr(errText(x));
    }
  };
  if (none || creating) {
    const profs = profiles.data?.profiles ?? [];
    return (
      <fieldset className="gp-firm-fieldset" aria-describedby={error ? 'period-err' : undefined}>
        <legend>{none ? 'No audit period yet · create one' : 'New audit period'}</legend>
        {err ? <Alert variant="error">{err}</Alert> : null}
        {profiles.data && profs.length === 0 ? (
          <p className="gp-firm-actions"><span>Set up a certification program first.</span> <Link to={`/w/${slug}/app/programs`}>Programs</Link></p>
        ) : (
          <div className="gp-firm-form--row">
            <Field id="np-profile" label="Program" error={tried && !p.profile_id ? 'Choose the program.' : null}>
              <Select value={p.profile_id} onChange={(e) => setP({ ...p, profile_id: e.target.value })}>
                <option value="">Choose…</option>
                {profs.map((x) => <option key={x.profile_id} value={x.profile_id}>{PROGRAM_LABEL[x.program] ?? x.program}{x.report_type === 'type2' ? ' Type II' : x.report_type === 'type1' ? ' Type I' : ''}</option>)}
              </Select>
            </Field>
            <Field id="np-start" label="Period starts" error={tried && !p.period_start ? 'Pick a start date.' : null}><Input type="date" value={p.period_start} onChange={(e) => setP({ ...p, period_start: e.target.value })} /></Field>
            <Field id="np-end" label="Period ends" error={tried && (!p.period_end || p.period_end <= p.period_start) ? 'Pick an end after the start.' : null}><Input type="date" value={p.period_end} onChange={(e) => setP({ ...p, period_end: e.target.value })} /></Field>
          </div>
        )}
        <div className="gp-firm-actions">
          <Button compact onClick={() => void create()}>Create period</Button>
          {!none ? <Button compact variant="ghost" onClick={() => setCreating(false)}>Cancel</Button> : null}
        </div>
        {error ? <p className="gp-firm-err" id="period-err">{error}</p> : null}
      </fieldset>
    );
  }
  return (
    <div className="gp-firm-form--row">
      <Field id="firm-period" label="Audit period" error={error}>
        <Select value={value} onChange={(e) => onChange(e.target.value)}>
          <option value="">Choose…</option>
          {(periods ?? []).map((x) => <option key={x.period_id} value={x.period_id}>{fmtRange(x.period_start, x.period_end)}</option>)}
        </Select>
      </Field>
      <div className="gp-firm-actions"><Button compact variant="ghost" onClick={() => setCreating(true)}>New period…</Button></div>
    </div>
  );
}

function RevokeDialog({ slug, engagementId, firm, onClose, onDone }: { slug: string; engagementId: string; firm: string; onClose: () => void; onDone: () => void }) {
  const [reason, setReason] = useState('');
  const [tried, setTried] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  return (
    <ActionDialog open title={`Revoke ${firm}’s access?`} submitLabel="Revoke access" danger busy={busy} error={err} onClose={onClose}
      onSubmit={async () => {
        setTried(true);
        if (reason.trim().length < 5) return false;
        setBusy(true);
        setErr(null);
        try {
          await clientFirmApi.revoke(slug, engagementId, reason.trim());
          onDone();
          onClose();
        } catch (e) {
          setErr(errText(e));
        } finally {
          setBusy(false);
        }
        return undefined;
      }}>
      <p>The firm stops seeing your requests, populations and evidence. It keeps its own working papers — read-only, partner and manager only — for its retention period, as audit firms must.</p>
      <Field id="revoke-reason" label="Reason" error={tried && reason.trim().length < 5 ? 'Give a reason (at least 5 characters).' : null}>
        <Input value={reason} onChange={(e) => setReason(e.target.value)} />
      </Field>
    </ActionDialog>
  );
}
